/**
 * Cómo se muestra un egreso —un gasto, un retiro, un sueldo, un pago a
 * proveedor— en Gastos, en Inicio y en la pestaña «Gastos» de Reportes.
 *
 * Una sola fila para las tres pantallas: el mismo gasto no puede decir una
 * cosa en Inicio y otra en Gastos.
 */

import type { ReactNode } from "react";
import { ArrowRightLeft, Ban, TrendingDown, Wallet } from "lucide-react";
import { cx } from "@/components/ui";
import { formatMoney, toNumber, type FormatoMoneda } from "@/lib/money";
import type { EgresoDelRango } from "@/lib/types";

/*
  ⚠️ LO PAGADO EN «POR PAGAR» ES GASTO, NO RETIRO.

  El dueño lo vio con el alquiler y las facturas de Andina y D1 saliendo como
  retiros. Ahora vienen del servidor como pago a proveedor —o mercancía, si la
  deuda nació de una entrada— y cuentan en «Gastado».
*/
export const ES_GASTO = new Set(["expense", "nomina", "pago_proveedor", "mercancia"]);

export const NOMBRE_DEL_EGRESO: Record<string, string> = {
  expense: "Gasto",
  nomina: "Nómina",
  pago_proveedor: "Pago a proveedor",
  mercancia: "Mercancía de una entrada",
  withdrawal: "Retiro",
};

export function esGasto(e: EgresoDelRango): boolean {
  return ES_GASTO.has(e.categoria);
}

/**
 * De qué caja y de qué turno salió: «Caja 1 · turno de Grecia Rubio».
 * `null` si no salió de una caja (apuntado sin caja abierta, sueldos, pagos).
 *
 * ⚠️ Lo pidieron clientes (2026-09-29): sin esto, un gasto de caja se veía
 * como uno más y creían que solo aparecía en el historial de la caja.
 */
export function deDondeSalio(e: EgresoDelRango): string | null {
  if (!e.caja) return null;
  return e.turno_de ? `${e.caja} · turno de ${e.turno_de}` : e.caja;
}

/** El tipo de egreso, en palabras: «Gasto», «Retiro», «Nómina»… */
export function tipoDeEgreso(e: EgresoDelRango): string {
  return NOMBRE_DEL_EGRESO[e.categoria] ?? (esGasto(e) ? "Gasto" : "Retiro");
}

/** La etiqueta de la caja, junto al gasto. */
export function InsigniaDeCaja({ egreso }: { egreso: EgresoDelRango }) {
  const origen = deDondeSalio(egreso);
  if (!origen) return null;
  return (
    <span
      className="inline-flex max-w-full items-center gap-1 rounded-full border border-line bg-surface-2 px-2 py-0.5 text-[11px] font-medium text-ink-soft"
      title="Registrado en este turno de caja"
    >
      <Wallet size={11} className="shrink-0" aria-hidden="true" />
      <span className="truncate">{origen}</span>
    </span>
  );
}

/** Un egreso en una lista: qué fue, cuándo, quién, de qué caja y cuánto. */
/** «50,00 USD (Efectivo) + 20.000,00 COP (Efectivo)»: las partes de un gasto. */
export function textoDeLasPartes(partes: NonNullable<EgresoDelRango["partes"]>): string {
  return partes
    .map(
      (p) =>
        `${toNumber(p.monto).toLocaleString("es-VE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${p.moneda}` +
        (p.metodo ? ` (${p.metodo})` : ""),
    )
    .join(" + ");
}

export function FilaDeEgreso({
  egreso: e,
  formato,
  monedaPrincipal,
  acciones,
  onAbrir,
}: {
  egreso: EgresoDelRango;
  formato: FormatoMoneda;
  /** La moneda del negocio: la original solo se repite si es otra. */
  monedaPrincipal: string;
  /** Editar y anular, en Gastos y solo con el permiso (2026-10-05). */
  acciones?: ReactNode;
  /**
   * Abre el detalle del movimiento, el mismo de Cuentas (2026-10-22). La fila
   * entera; los botones de `acciones` no lo disparan.
   */
  onAbrir?: () => void;
}) {
  // ⚠️ La nómina es GASTO, no retiro: un sueldo es dinero que salió del
  // negocio, no dinero que cambió de sitio.
  const gasto = esGasto(e);
  const anulado = Boolean(e.anulado);
  const fecha = new Date(e.fecha);
  return (
    <div
      className={cx(
        "flex items-center gap-3 px-4 py-3",
        onAbrir && "cursor-pointer transition-colors hover:bg-surface-hover focus-visible:bg-surface-hover focus-visible:outline-none",
      )}
      {...(onAbrir
        ? {
            role: "button",
            tabIndex: 0,
            "aria-label": `Ver el detalle: ${e.descripcion}`,
            onClick: onAbrir,
            onKeyDown: (ev: React.KeyboardEvent) => {
              if (ev.target !== ev.currentTarget) return;
              if (ev.key === "Enter" || ev.key === " ") {
                ev.preventDefault();
                onAbrir();
              }
            },
          }
        : {})}
    >
      <div
        className={cx(
          "flex size-9 shrink-0 items-center justify-center rounded-[var(--radius-control)]",
          anulado
            ? "bg-surface-2 text-faint"
            : gasto
              ? "bg-danger-soft text-danger"
              : "bg-surface-2 text-muted",
        )}
      >
        {anulado ? (
          <Ban size={16} />
        ) : gasto ? (
          <TrendingDown size={16} />
        ) : (
          <ArrowRightLeft size={16} />
        )}
      </div>

      <div className="min-w-0 flex-1">
        <p
          className={cx(
            "truncate text-sm font-medium",
            anulado ? "text-muted line-through" : "text-ink",
          )}
        >
          {e.descripcion}
        </p>
        <p className="text-xs text-muted">
          {tipoDeEgreso(e)} ·{" "}
          {fecha.toLocaleDateString("es-VE", { day: "2-digit", month: "short" })}{" "}
          {fecha.toLocaleTimeString("es-VE", { hour: "2-digit", minute: "2-digit" })}
          {e.quien ? ` · ${e.quien}` : ""}
          {e.metodo ? ` · ${e.metodo}` : ""}
        </p>
        {deDondeSalio(e) && (
          <div className="mt-1">
            <InsigniaDeCaja egreso={e} />
          </div>
        )}
        {anulado && (
          <p className="mt-1 text-xs text-danger">
            Anulado{e.anulado_por ? ` por ${e.anulado_por}` : ""}
            {e.motivo_anulacion ? `: ${e.motivo_anulacion}` : ""}
          </p>
        )}
      </div>

      <div className="shrink-0 text-right">
        <p
          className={cx(
            "tabular text-sm font-semibold",
            anulado ? "text-faint line-through" : gasto ? "text-danger" : "text-ink",
          )}
        >
          −{formatMoney(e.monto_en_principal, formato)}
        </p>
        {/* Un gasto en partes (2026-10-12): cada moneda con su monto. */}
        {e.partes && e.partes.length > 1 ? (
          <p className="tabular max-w-[16rem] text-[11px] text-muted">
            {e.partes
              .map(
                (p) =>
                  `${toNumber(p.monto).toLocaleString("es-VE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${p.moneda}`,
              )
              .join(" + ")}
          </p>
        ) : /* La moneda original solo si NO es la del negocio: repetir «$»
            debajo de «$» es ruido. */
        e.moneda !== monedaPrincipal && (
          <p className="tabular text-[11px] text-muted">
            {toNumber(e.monto).toLocaleString("es-VE", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}{" "}
            {e.moneda}
          </p>
        )}
      </div>

      {acciones && (
        // Editar y anular no abren el detalle: tienen que exigir apuntar.
        <div className="flex shrink-0 items-center gap-0.5" onClick={(ev) => ev.stopPropagation()}>
          {acciones}
        </div>
      )}
    </div>
  );
}
